/**
 * borrowerProfile.js — the canonical borrower profile for the decision engine.
 *
 * The profile uses the workbook's UI Input Map vocabulary (loanPurpose,
 * occupancy, creditBand, …). UI flows never talk to the engine in their own
 * answer keys; they adapt into this shape (see profileFromReadinessAnswers).
 *
 * Convention: `undefined` means "not yet known" — rules that depend on an
 * unknown input report `needs_data`, never automatic ineligibility.
 *
 * @typedef {Object} BorrowerProfile
 * @property {'purchase'|'refinance'=} loanPurpose
 * @property {'rate-term'|'cash-out'=} refiKind
 * @property {'conventional'|'fha'|'va'|'usda'|'other'=} existingLoanType
 * @property {'primary'|'second-home'|'investment'=} occupancy
 * @property {'sfr'|'condo'|'townhouse'|'multi'|'manufactured'|'coop'|'mixed-use'=} propertyType
 * @property {number=} units
 * @property {number=} propertyValue      purchase price or estimated value
 * @property {number=} downPayment        purchase only
 * @property {number=} currentLoanBalance refinance only
 * @property {number=} loanAmount         derived
 * @property {number=} ltv                derived (percent)
 * @property {{min:number,max:number}=} creditBand
 * @property {boolean=} creditUnsure
 * @property {'w2'|'self-employed'|'retired'|'other'=} incomeType
 * @property {number=} annualIncome
 * @property {number=} monthlyDebts
 * @property {'eligible'|'not-eligible'=} veteranStatus
 * @property {boolean=} firstTimeHomebuyer
 * @property {string=} state
 * @property {string=} county
 * @property {'yes'|'no'|'unsure'=} ruralEligibility
 * @property {boolean=} renovationNeeded
 * @property {boolean=} constructionNeeded
 * @property {'lowest-payment'|'balanced'|'fast-payoff'|'build-equity'|'lowest-interest'|'flexibility'=} primaryMortgageGoal
 * @property {number=} expectedHoldYears  derived from expectedYearsInHome
 * @property {number=} maxComfortablePayment
 * @property {'essential'|'important'|'flexible'|'comfortable'=} paymentStabilityPreference
 * @property {'no'|'maybe'|'yes'=} rateRiskTolerance
 * @property {'no'|'occasionally'|'monthly'|'large-periodic'=} plannedExtraPayments
 * @property {number=} conformingLimit    derived from state/county
 */

import countyLimits from '../data/countyConformingLimits.json' with { type: 'json' };
import { numFrom } from '../config/readinessQuestions.js';

const CREDIT_BANDS = {
  '760+': { min: 760, max: 850 },
  '700-759': { min: 700, max: 759 },
  '660-699': { min: 660, max: 699 },
  '600-659': { min: 600, max: 659 },
  '<600': { min: 300, max: 599 },
};

const HOLD_YEARS = {
  '<3': 2, '3-5': 4, '6-7': 6.5, '8-10': 9, '10+': 15,
};

/** Readiness county chip ids → county names in countyConformingLimits.json. */
const CA_COUNTY_NAMES = {
  'los-angeles': 'Los Angeles', orange: 'Orange', 'san-diego': 'San Diego',
  sacramento: 'Sacramento', placer: 'Placer', fresno: 'Fresno', humboldt: 'Humboldt',
};

function conformingLimitFor(state, countyName) {
  if (state && countyName) {
    const key = `${state.toUpperCase()}|${countyName}`;
    if (countyLimits.highCost[key]) return countyLimits.highCost[key];
  }
  return countyLimits.baseline;
}

/** USDA rural flag from the readiness county option metadata. */
function ruralFromCounty(county) {
  const rural = { fresno: 'yes', humboldt: 'yes', sacramento: 'unsure', placer: 'unsure', other: 'unsure' };
  if (!county) return undefined;
  return rural[county] ?? 'no';
}

/**
 * Adapt readiness chat answers (src/config/readinessQuestions.js ids) into the
 * canonical profile, deriving loanAmount, LTV, and the conforming limit.
 */
export function profileFromReadinessAnswers(answers = {}) {
  const p = {};
  if (answers.purpose) p.loanPurpose = answers.purpose === 'purchase' ? 'purchase' : 'refinance';

  if (answers.propertyUse) {
    p.occupancy = answers.propertyUse === 'primary' ? 'primary'
      : answers.propertyUse === 'secondary' ? 'second-home' : 'investment';
  }

  if (answers.propertyType) {
    p.propertyType = answers.propertyType === 'single-family' ? 'sfr'
      : answers.propertyType === 'multi' ? 'multi' : answers.propertyType;
    p.units = answers.propertyType === 'multi' ? 2 : 1; // conservative: 2–4 treated as 2 unless refined
  }

  const value = answers.value !== undefined ? numFrom(answers.value) : undefined;
  if (value) p.propertyValue = value;

  if (answers.down !== undefined && value) {
    const down = numFrom(answers.down);
    if (p.loanPurpose === 'refinance') {
      p.currentLoanBalance = down; // refi wording: remaining balance
      p.loanAmount = down;
      p.ltv = value > 0 ? (down / value) * 100 : undefined;
    } else {
      p.downPayment = down;
      p.loanAmount = Math.max(value - down, 0);
      p.ltv = value > 0 ? (p.loanAmount / value) * 100 : undefined;
    }
  }

  if (answers.credit) {
    if (answers.credit === 'unsure') {
      p.creditUnsure = true;
      p.creditBand = { min: 660, max: 739 }; // stated assumption, surfaced as a caution
    } else {
      p.creditBand = CREDIT_BANDS[answers.credit];
    }
  }

  if (answers.employment) {
    p.incomeType = answers.employment === 'w2' ? 'w2'
      : answers.employment === 'self' ? 'self-employed' : answers.employment;
  }
  if (answers.income !== undefined) p.annualIncome = numFrom(answers.income);
  if (answers.debts !== undefined) p.monthlyDebts = numFrom(answers.debts);
  if (answers.military) p.veteranStatus = answers.military === 'yes' ? 'eligible' : 'not-eligible';
  if (answers.firstTime) p.firstTimeHomebuyer = answers.firstTime === 'yes';

  if (answers.state) p.state = answers.state === 'ca' ? 'CA' : 'other';
  if (answers.county) {
    p.county = CA_COUNTY_NAMES[answers.county] || answers.county;
    p.ruralEligibility = ruralFromCounty(answers.county);
  }
  p.conformingLimit = conformingLimitFor(p.state === 'CA' ? 'CA' : null, p.county);

  // Newer engine-driven questions (canonical ids used directly as answer keys)
  if (answers.existingLoanType) p.existingLoanType = answers.existingLoanType;
  if (answers.refiKind) p.refiKind = answers.refiKind;
  if (answers.renovationNeeded) p.renovationNeeded = answers.renovationNeeded === 'yes';
  if (answers.primaryMortgageGoal) p.primaryMortgageGoal = answers.primaryMortgageGoal;
  if (answers.expectedYearsInHome) {
    p.expectedHoldYears = HOLD_YEARS[answers.expectedYearsInHome]; // 'unsure' → undefined
    p.expectedHoldAnswered = true;
  }
  if (answers.maxComfortablePayment !== undefined) {
    const v = numFrom(answers.maxComfortablePayment);
    if (v > 0) p.maxComfortablePayment = v;
    p.comfortAnswered = true;
  }
  if (answers.paymentStabilityPreference) p.paymentStabilityPreference = answers.paymentStabilityPreference;
  if (answers.rateRiskTolerance) p.rateRiskTolerance = answers.rateRiskTolerance;
  if (answers.plannedExtraPayments) p.plannedExtraPayments = answers.plannedExtraPayments;

  return p;
}

/** Immutable "what-if": profile with one canonical key applied. */
export function profileWith(profile, key, value) {
  return { ...profile, [key]: value };
}
